import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithReauth } from '@/redux/service/baseQueryWithReauth'
import dayjs from 'dayjs'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export const dateTimeFormat = (date: string) => {
  return new Date(date).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
export const dateFormat = (date: string) => {
  return new Date(date).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
}
// dd-MM-yyyy — the format used across the app's date pickers/tables (see
// ExpenseRatioHistory.tsx). Leave-related screens standardized on this too.
export const dateFormatDMY = (date: string | Date | null | undefined) => {
  if (!date) return '—'
  const d = dayjs(date)
  return d.isValid() ? d.format('DD-MM-YYYY') : '—'
}
export const dateTimeFormatDMY = (date: string | Date | null | undefined) => {
  if (!date) return '—'
  const d = dayjs(date)
  return d.isValid() ? d.format('DD-MM-YYYY, HH:mm') : '—'
}
/** Display format for a date, converted to IST (e.g. "1 Jan 2026"). Backend
 * stores dates as UTC timestamps of IST midnight, so this must go through
 * Asia/Kolkata rather than reading the UTC calendar date. */
export const istDate = (dateString: string | null | undefined) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
/** Same IST conversion as {@link istDate}, as 'yyyy-MM-dd' for form inputs
 * and date pickers. */
export const toISTDateOnly = (dateString?: string | null) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-CA', {
        timeZone: 'Asia/Kolkata',
      })
    : ''
export const getTagStyle = (text: string) => {
  const styles = {
    '1st round qa': { bg: '#3b82f6', color: '#fff' },
    '2nd round qa': { bg: '#ef4444', color: '#fff' },
    'client feedback': { bg: '#6b7280', color: '#fff' },
    content: { bg: '#065f46', color: '#fff' },
    'cover-tracking': { bg: '#1f2937', color: '#fff' },
    design: { bg: '#7c3aed', color: '#fff' },
    dev: { bg: '#d97706', color: '#fff' },
    development: { bg: '#2563eb', color: '#fff' },
    devops: { bg: '#0284c7', color: '#fff' },
    doc: { bg: '#65a30d', color: '#fff' },

    figma: { bg: '#9333ea', color: '#fff' },
    figmafi: { bg: '#a855f7', color: '#fff' },
    figms: { bg: '#7e22ce', color: '#fff' },
    freelancing: { bg: '#0f766e', color: '#fff' },
    maintenance: { bg: '#ca8a04', color: '#fff' },
    me: { bg: '#374151', color: '#fff' },
    meeting: { bg: '#1e40af', color: '#fff' },
    'power cut': { bg: '#000000', color: '#fff' },
    'power-cut': { bg: '#000000', color: '#fff' },
    productivity: { bg: '#0369a1', color: '#fff' },

    support: { bg: '#16a34a', color: '#fff' },
    training: { bg: '#7c3aed', color: '#fff' },
    work: { bg: '#9d174d', color: '#fff' },
    'work fixes': { bg: '#475569', color: '#fff' },
    complete: { bg: '#2A8559', color: '#F2F2F2' },
    'in review': { bg: '#8457EA', color: '#F2F2F2' },
    'to do': { bg: '#9CA3AF', color: '#F2F2F2' },
    'in progress': { bg: '#387BEA', color: '#F2F2F2' },
  }

  return (
    styles[text.toLowerCase() as keyof typeof styles] || {
      bg: '#374151',
      color: '#fff',
    }
  )
}
export const formatHours = (hours?: number | string | null) => {
  const value = Number(hours)
  if (!Number.isFinite(value) || value === 0) return '—'
  const rounded = Math.round(value * 100) / 100
  return rounded.toString()
}

export const formatSeconds = (seconds?: number | string | null) => {
  const total = Number(seconds)
  if (!Number.isFinite(total) || total <= 0) return '—'
  return formatHours(total / 3600)
}
export const toTitleCase = (value?: string | null) => {
  if (!value) return value
  return value.replace(
    /\w\S*/g,
    word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  )
}

export const formatCurrency = (value?: number | string | null) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return '$0.00'
  return `$${num.toFixed(2)}`
}
export const formatTime12 = (date: string) =>
  new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  })
export const formatDate = (date: string) => {
  return dayjs.utc(date).format('ddd MMM DD YYYY')
}
export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Employees',
    'Projects',
    'ProjectDetails',
    'ProjectPage',
    'DashboardCounts',
    'DataScope',
    'Company',
    'Teams',
    'ExpenseRatioHistory',
    'AdminNotes',
    'ProjectNotes',
    'Skills',
    'SavedFilters',
    'Vendors',
    'VendorHours',
    'VendorPayments',
    'LeaveBalances',
    'LeaveRequests',
    'Profile',
    'LeavePolicy',
  ],
  endpoints: () => ({}),
})
